import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Zap, Lock, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import TurnstileWidget, { isTurnstileEnabled } from '../../components/auth/TurnstileWidget';

// Shell must be defined OUTSIDE the component to avoid re-mount on state change
const Shell = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #0a0f1a 100%)' }}>
    <div className="absolute inset-0 pointer-events-none opacity-50"
      style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }}
    />
    <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-500/[0.06] rounded-full blur-[100px]" />
    <div className="relative z-10 w-full max-w-md px-4">
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden">
        {children}
      </div>
    </div>
  </div>
);

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  const requirements = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
    { label: 'One number', test: (p) => /[0-9]/.test(p) },
    { label: 'One special character', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
  ];

  const allRequirementsMet = requirements.every((r) => r.test(password));
  const passwordsMatch = password === confirmPassword && password.length > 0;

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) { setVerifying(false); setTokenValid(false); return; }
      try {
        const result = await authApi.verifyResetToken(token);
        setTokenValid(result.valid);
        setTokenEmail(result.email || '');
      } catch { setTokenValid(false); }
      finally { setVerifying(false); }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRequirementsMet) { toast.error('Please meet all password requirements'); return; }
    if (!passwordsMatch) { toast.error('Passwords do not match'); return; }
    if (isTurnstileEnabled && !turnstileToken) { toast.error('Please complete the verification challenge'); return; }
    setLoading(true);
    try {
      await authApi.resetPassword(token, password, turnstileToken);
      setResetSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to reset password');
      turnstileRef.current?.reset();
    } finally { setLoading(false); }
  };

  if (verifying) {
    return (
      <Shell>
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto" />
          <p className="mt-4 text-white/40">Verifying reset link...</p>
        </div>
      </Shell>
    );
  }

  if (!tokenValid && !resetSuccess) {
    return (
      <Shell>
        <div className="p-8 text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Invalid Reset Link</h2>
            <p className="text-sm text-white/40 mt-2">This link is invalid or has expired.</p>
          </div>
          <div className="space-y-2">
            <Link to="/forgot-password" className="block">
              <Button className="w-full bg-blue-600 hover:bg-blue-500 text-white" data-testid="request-new-link-btn">Request New Link</Button>
            </Link>
            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full text-white/40 hover:text-white" data-testid="back-to-login-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
              </Button>
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  if (resetSuccess) {
    return (
      <Shell>
        <div className="p-8 text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Password Reset!</h2>
            <p className="text-sm text-white/40 mt-2">You can now sign in with your new password.</p>
          </div>
          <Button className="w-full bg-blue-600 hover:bg-blue-500 text-white" onClick={() => navigate('/login')} data-testid="go-to-login-btn">Sign In</Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="p-8">
        <div className="text-center mb-8">
          <div className="mx-auto w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/20">
            <Zap className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Reset Password</h1>
          <p className="text-sm text-white/30 mt-1">Create a new password for {tokenEmail}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs text-white/40">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                className="pl-10 pr-10 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-blue-500/50 h-11"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="new-password-input"
              />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-white/40">Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <Input
                type={showConfirm ? 'text' : 'password'}
                placeholder="••••••••"
                className="pl-10 pr-10 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-blue-500/50 h-11"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60" onClick={() => setShowConfirm(!showConfirm)}>
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3 space-y-1.5">
            <p className="text-[10px] font-medium text-white/40">Password requirements:</p>
            {requirements.map((req, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                {req.test(password) ? <CheckCircle className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-white/20" />}
                <span className={req.test(password) ? 'text-emerald-400' : 'text-white/30'}>{req.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-xs pt-1">
              {passwordsMatch ? <CheckCircle className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-white/20" />}
              <span className={passwordsMatch ? 'text-emerald-400' : 'text-white/30'}>Passwords match</span>
            </div>
          </div>

          <div className="flex justify-center">
            <TurnstileWidget ref={turnstileRef} action="forgot_password" onVerify={setTurnstileToken} theme="dark" />
          </div>

          <Button
            type="submit"
            disabled={loading || !allRequirementsMet || !passwordsMatch || (isTurnstileEnabled && !turnstileToken)}
            className="w-full h-11 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium shadow-lg shadow-blue-500/20 disabled:opacity-50"
            data-testid="reset-password-btn">
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>

          <Link to="/login" className="block">
            <Button variant="ghost" className="w-full text-white/40 hover:text-white" type="button">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
            </Button>
          </Link>
        </form>
      </div>
      <div className="px-8 py-4 border-t border-white/[0.04] text-center">
        <p className="text-[11px] text-white/20">SpillConsult Hub</p>
      </div>
    </Shell>
  );
}
